import React, { useEffect, useMemo, useState } from 'react';
import type { Me } from '../App';
import { api, ApiError } from '../api';
import { Badge, deliveryTone } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';

interface Status {
  build: string;
  env: string;
  appUrl: string;
  smtp: { configured: boolean; host: string | null };
  imap: ImapStatus;
  queue: { ok: boolean; waiting?: number; active?: number; failed?: number; message?: string };
  stats: { users: number; events: number; deliveries: number; pendingDeliveries: number; failedDeliveries: number };
}

interface ImapPollResult {
  enabled: boolean;
  seenMessages: number;
  replyParts: number;
  ingested: number;
  bounces: number;
  failed: number;
}

interface ImapStatus {
  enabled: boolean;
  configured: boolean;
  running: boolean;
  host?: string | null;
  user?: string | null;
  folder: string;
  archiveFolder?: string | null;
  postProcessAction: string;
  intervalMs: number;
  lastStartedAt?: string | null;
  lastFinishedAt?: string | null;
  lastError?: string | null;
  lastResult?: ImapPollResult | null;
}

interface User {
  id: string;
  email: string;
  name?: string | null;
  role: 'ADMIN' | 'USER';
  isActive: boolean;
  organizerEmail?: string | null;
  createdAt: string;
  _count: { calendars: number; groupMembers: number };
}

interface Group {
  id: string;
  name: string;
  description?: string | null;
  members: Array<{ id: string; role: 'OWNER' | 'MEMBER'; user: Pick<User, 'id' | 'email' | 'name' | 'role' | 'isActive'> }>;
  _count: { calendarShares: number };
}

interface Delivery {
  id: string;
  toEmail: string;
  status: string;
  method: string;
  errorMessage?: string | null;
  queuedAt: string;
  sentAt?: string | null;
  failedAt?: string | null;
  event?: { summary: string } | null;
}

interface MailSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  hasPassword: boolean;
  from: string;
  replyTo: string;
}

interface MailTemplate {
  kind: string;
  subject: string;
  text: string;
  html?: string | null;
  enabled?: boolean;
}

interface MailTemplatePreview {
  subject: string;
  text: string;
  html?: string | null;
}

interface HistoryItem {
  id: string;
  action: string;
  message: string;
  createdAt: string;
  user?: { email: string; name?: string | null } | null;
  event?: { summary: string } | null;
}

interface BounceLog {
  id: string;
  toEmail?: string | null;
  fromEmail?: string | null;
  subject?: string | null;
  diagnostic?: string | null;
  createdAt: string;
  delivery?: { event?: { summary: string } | null } | null;
}

interface CalDavLog {
  id: string;
  email?: string | null;
  method: string;
  path: string;
  statusCode: number;
  outcome: string;
  depth?: string | null;
  userAgent?: string | null;
  remoteAddress?: string | null;
  appPasswordHint?: string | null;
  createdAt: string;
  user?: { email: string; name?: string | null } | null;
}

interface AuditItem {
  id: string;
  type: 'event' | 'caldav' | 'delivery' | 'bounce' | 'subscription';
  severity: 'info' | 'success' | 'warning' | 'danger';
  status: string;
  title: string;
  detail?: string | null;
  actor?: string | null;
  target?: string | null;
  eventSummary?: string | null;
  method?: string | null;
  path?: string | null;
  client?: string | null;
  createdAt: string;
}

interface AuditResponse {
  items: AuditItem[];
  totals: { total: number; danger: number; warning: number; byType: Record<string, number> };
  generatedAt: string;
}

type AdminTab = 'audit' | 'users' | 'groups' | 'mail' | 'history' | 'caldav';

export function AdminPage({ me }: { me: Me }) {
  const [tab, setTab] = useState<AdminTab>('audit');
  const [status, setStatus] = useState<Status | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [mailSettings, setMailSettings] = useState<MailSettings | null>(null);
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [bounces, setBounces] = useState<BounceLog[]>([]);
  const [caldavLogs, setCaldavLogs] = useState<CalDavLog[]>([]);
  const [audit, setAudit] = useState<AuditResponse | null>(null);
  const [imapStatus, setImapStatus] = useState<ImapStatus | null>(null);
  const [auditFilter, setAuditFilter] = useState({ type: '', status: '', q: '', limit: '150' });
  const [mailForm, setMailForm] = useState({ host: '', port: 587, secure: false, user: '', pass: '', from: '', replyTo: '' });
  const [templateKind, setTemplateKind] = useState('INVITATION');
  const [templatePreview, setTemplatePreview] = useState<MailTemplatePreview | null>(null);
  const [templateTestTo, setTemplateTestTo] = useState('');
  const [testTo, setTestTo] = useState('');
  const [userOpen, setUserOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [userForm, setUserForm] = useState({ email: '', name: '', password: '', role: 'USER' as 'USER' | 'ADMIN', organizerEmail: '', isActive: true });
  const [groupForm, setGroupForm] = useState({ name: '', description: '', userId: '', memberRole: 'MEMBER' as 'OWNER' | 'MEMBER' });
  const toast = useToast();

  const activeUsers = useMemo(() => users.filter((u) => u.isActive), [users]);
  const auditQuery = useMemo(() => auditQueryString(auditFilter), [auditFilter]);
  const auditCsvHref = `/api/admin/audit.csv?${auditQuery}`;

  const load = async () => {
    const [s, u, g, d, ms, mt, h, b, cl, a, imap] = await Promise.all([
      api.get<Status>('/api/admin/status'),
      api.get<User[]>('/api/admin/users'),
      api.get<Group[]>('/api/admin/groups'),
      api.get<Delivery[]>('/api/admin/deliveries'),
      api.get<MailSettings>('/api/admin/mail-settings'),
      api.get<{ templates: MailTemplate[] }>('/api/admin/mail-templates'),
      api.get<HistoryItem[]>('/api/admin/history'),
      api.get<BounceLog[]>('/api/admin/bounces'),
      api.get<CalDavLog[]>('/api/admin/caldav-logs'),
      api.get<AuditResponse>(`/api/admin/audit?${auditQuery}`),
      api.get<ImapStatus>('/api/admin/imap/status'),
    ]);
    setStatus(s);
    setUsers(u);
    setGroups(g);
    setDeliveries(d);
    setMailSettings(ms);
    setMailForm({ host: ms.host, port: ms.port, secure: ms.secure, user: ms.user, pass: '', from: ms.from, replyTo: ms.replyTo });
    setTemplates(mt.templates);
    setHistory(h);
    setBounces(b);
    setCaldavLogs(cl);
    setAudit(a);
    setImapStatus(imap);
  };

  useEffect(() => {
    if (me.role === 'ADMIN') load();
  }, [me.role]);

  if (me.role !== 'ADMIN') return <div className="card">Kein Zugriff.</div>;

  const openNewUser = () => {
    setEditingUser(null);
    setUserForm({ email: '', name: '', password: '', role: 'USER', organizerEmail: '', isActive: true });
    setUserOpen(true);
  };

  const openEditUser = (user: User) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      name: user.name ?? '',
      password: '',
      role: user.role,
      organizerEmail: user.organizerEmail ?? user.email,
      isActive: user.isActive,
    });
    setUserOpen(true);
  };

  const saveUser = async () => {
    try {
      if (editingUser) {
        await api.patch(`/api/admin/users/${editingUser.id}`, {
          name: userForm.name || null,
          role: userForm.role,
          organizerEmail: userForm.organizerEmail || null,
          isActive: userForm.isActive,
          ...(userForm.password ? { password: userForm.password } : {}),
        });
        toast.success('Benutzer gespeichert');
      } else {
        await api.post('/api/admin/users', {
          email: userForm.email,
          name: userForm.name,
          password: userForm.password,
          role: userForm.role,
          organizerEmail: userForm.organizerEmail || undefined,
        });
        toast.success('Benutzer angelegt');
      }
      setUserOpen(false);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Benutzer konnte nicht gespeichert werden');
    }
  };

  const deleteUser = async (user: User) => {
    await api.delete(`/api/admin/users/${user.id}`);
    toast.success('Benutzer geloescht');
    load();
  };

  const openNewGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '', userId: '', memberRole: 'MEMBER' });
    setGroupOpen(true);
  };

  const openEditGroup = (group: Group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, description: group.description ?? '', userId: '', memberRole: 'MEMBER' });
    setGroupOpen(true);
  };

  const saveGroup = async () => {
    try {
      if (editingGroup) {
        await api.patch(`/api/admin/groups/${editingGroup.id}`, { name: groupForm.name, description: groupForm.description || null });
        toast.success('Gruppe gespeichert');
      } else {
        await api.post('/api/admin/groups', { name: groupForm.name, description: groupForm.description || null });
        toast.success('Gruppe angelegt');
      }
      setGroupOpen(false);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Gruppe konnte nicht gespeichert werden');
    }
  };

  const addMember = async (group: Group) => {
    if (!groupForm.userId) return toast.error('Benutzer waehlen');
    await api.post(`/api/admin/groups/${group.id}/members`, { userId: groupForm.userId, role: groupForm.memberRole });
    setGroupForm({ ...groupForm, userId: '' });
    toast.success('Mitglied hinzugefuegt');
    load();
  };

  const removeMember = async (group: Group, userId: string) => {
    await api.delete(`/api/admin/groups/${group.id}/members/${userId}`);
    toast.success('Mitglied entfernt');
    load();
  };

  const verifySmtp = async () => {
    const r = await api.post<{ ok: boolean; message: string }>('/api/admin/smtp/verify');
    r.ok ? toast.success(r.message) : toast.error(r.message);
  };

  const sendTest = async () => {
    await api.post('/api/admin/smtp/test', { to: testTo });
    toast.success('Test-Mail gesendet');
    load();
  };

  const saveMailSettings = async () => {
    const saved = await api.patch<MailSettings>('/api/admin/mail-settings', mailForm);
    setMailSettings(saved);
    setMailForm({ ...mailForm, pass: '' });
    toast.success('Mailserver gespeichert');
  };

  const selectedTemplate = templates.find((t) => t.kind === templateKind);
  const updateTemplate = (patch: Partial<MailTemplate>) => {
    setTemplates((items) => items.map((t) => (t.kind === templateKind ? { ...t, ...patch } : t)));
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    await api.patch(`/api/admin/mail-templates/${selectedTemplate.kind}`, {
      subject: selectedTemplate.subject,
      text: selectedTemplate.text,
      html: selectedTemplate.html ?? null,
      enabled: selectedTemplate.enabled !== false,
    });
    toast.success('Mailtemplate gespeichert');
    load();
  };

  const previewTemplate = async (kind = templateKind) => {
    setTemplatePreview(await api.get<MailTemplatePreview>(`/api/admin/mail-templates/${kind}/preview`));
  };

  const sendTemplateTest = async () => {
    if (!templateTestTo) return toast.error('Empfaenger fuer Testversand eintragen');
    await api.post(`/api/admin/mail-templates/${templateKind}/test`, { to: templateTestTo });
    toast.success('Template-Testmail gesendet');
    load();
  };

  const loadAudit = async () => {
    setAudit(await api.get<AuditResponse>(`/api/admin/audit?${auditQuery}`));
  };
  const loadImapStatus = async () => {
    setImapStatus(await api.get<ImapStatus>('/api/admin/imap/status'));
  };
  const adminTabs: AdminTab[] = ['audit', 'users', 'groups', 'mail', 'caldav', 'history'];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h3">Admin</h1>
        <div className="flex gap-2">
          {adminTabs.map((item) => (
            <button key={item} className={`btn ${tab === item ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(item)}>
              {item === 'audit' ? 'Audit' : item === 'users' ? 'Benutzer' : item === 'groups' ? 'Gruppen' : item === 'mail' ? 'Mail & System' : item === 'caldav' ? 'CalDAV-Logs' : 'Historie'}
            </button>
          ))}
        </div>
      </div>

      {status && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Benutzer" value={String(status.stats.users)} />
          <Kpi label="Termine" value={String(status.stats.events)} />
          <Kpi label="Queue" value={status.queue.ok ? `w:${status.queue.waiting} a:${status.queue.active} f:${status.queue.failed}` : 'offline'} tone={status.queue.ok ? undefined : 'warning'} />
          <Kpi label="SMTP" value={status.smtp.configured ? 'aktiv' : 'offen'} tone={status.smtp.configured ? undefined : 'warning'} />
          <Kpi label="IMAP" value={status.imap.configured ? (status.imap.running ? 'laeuft' : 'bereit') : status.imap.enabled ? 'unvollstaendig' : 'aus'} tone={status.imap.configured ? undefined : 'warning'} />
        </div>
      )}

      {tab === 'audit' && (
        <section className="grid gap-4">
          <div className="card">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <h2 className="text-h4">Zentrale Diagnose</h2>
                <p className="mt-1 text-small text-ink-muted">Event-History, CalDAV, Mailversand, Bounces und Abos in einer Ansicht.</p>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[160px,160px,minmax(220px,1fr),120px,auto,auto] sm:items-end">
                <div>
                  <label className="label">Quelle</label>
                  <select className="select" value={auditFilter.type} onChange={(e) => setAuditFilter({ ...auditFilter, type: e.target.value })}>
                    <option value="">Alle</option>
                    <option value="event">Historie</option>
                    <option value="caldav">CalDAV</option>
                    <option value="delivery">Mail</option>
                    <option value="bounce">Bounce</option>
                    <option value="subscription">Abo</option>
                  </select>
                </div>
                <div>
                  <label className="label">Status</label>
                  <input className="input" value={auditFilter.status} onChange={(e) => setAuditFilter({ ...auditFilter, status: e.target.value })} placeholder="FAILED, 401..." />
                </div>
                <div>
                  <label className="label">Suche</label>
                  <input className="input" value={auditFilter.q} onChange={(e) => setAuditFilter({ ...auditFilter, q: e.target.value })} placeholder="E-Mail, Pfad, Termin, Fehler" />
                </div>
                <div>
                  <label className="label">Limit</label>
                  <input type="number" min={1} max={500} className="input" value={auditFilter.limit} onChange={(e) => setAuditFilter({ ...auditFilter, limit: e.target.value })} />
                </div>
                <button className="btn btn-secondary" onClick={loadAudit}>Filtern</button>
                <a className="btn btn-primary" href={auditCsvHref}>CSV</a>
              </div>
            </div>
          </div>

          {audit && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Kpi label="Audit-Eintraege" value={String(audit.totals.total)} />
              <Kpi label="Fehler" value={String(audit.totals.danger)} tone={audit.totals.danger ? 'warning' : undefined} />
              <Kpi label="Warnungen" value={String(audit.totals.warning)} tone={audit.totals.warning ? 'warning' : undefined} />
              <Kpi label="CalDAV" value={String(audit.totals.byType.caldav ?? 0)} />
            </div>
          )}

          <section className="card !p-0 overflow-hidden">
            <table className="w-full text-body">
              <thead className="bg-surface-elevated text-meta uppercase tracking-[0.16em] text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-3">Zeit</th>
                  <th className="text-left px-4 py-3">Quelle</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Ereignis</th>
                  <th className="text-left px-4 py-3">Akteur / Ziel</th>
                </tr>
              </thead>
              <tbody>
                {audit?.items.map((item) => (
                  <tr key={`${item.type}-${item.id}`} className="table-row align-top">
                    <td className="px-4 py-3 font-mono text-small text-ink-muted">{new Date(item.createdAt).toLocaleString('de-DE')}</td>
                    <td className="px-4 py-3"><Badge tone="muted">{auditTypeLabel(item.type)}</Badge></td>
                    <td className="px-4 py-3"><Badge tone={auditTone(item.severity)}>{item.status}</Badge></td>
                    <td className="px-4 py-3 min-w-0">
                      <div className="font-medium break-words">{item.title}</div>
                      {(item.detail || item.client || item.path) && (
                        <div className="mt-1 max-w-[680px] break-words font-mono text-[11px] text-ink-muted">
                          {item.detail ?? item.client ?? item.path}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-small text-ink-secondary">
                      <div className="font-mono break-words">{item.actor ?? '—'}</div>
                      {(item.target || item.eventSummary) && <div className="mt-1 break-words">{item.target ?? item.eventSummary}</div>}
                    </td>
                  </tr>
                ))}
                {(!audit || audit.items.length === 0) && <tr><td colSpan={5} className="text-center text-ink-muted py-8 text-small">Keine Audit-Eintraege fuer diese Filter.</td></tr>}
              </tbody>
            </table>
          </section>
        </section>
      )}

      {tab === 'users' && (
        <section className="card !p-0 overflow-hidden">
          <div className="p-5 flex items-center justify-between border-b border-border">
            <h2 className="text-h4">Benutzer</h2>
            <button className="btn btn-primary" onClick={openNewUser}>+ Benutzer</button>
          </div>
          <table className="w-full text-body">
            <thead className="bg-surface-elevated text-meta uppercase tracking-[0.16em] text-ink-muted">
              <tr>
                <th className="text-left px-4 py-3">E-Mail</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Rolle</th>
                <th className="text-left px-4 py-3">Gruppen</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="table-row">
                  <td className="px-4 py-3 font-mono text-small">{u.email}</td>
                  <td className="px-4 py-3">{u.name ?? '—'}</td>
                  <td className="px-4 py-3"><Badge tone={u.role === 'ADMIN' ? 'info' : 'muted'}>{u.role}</Badge></td>
                  <td className="px-4 py-3">{u._count.groupMembers}</td>
                  <td className="px-4 py-3"><Badge tone={u.isActive ? 'success' : 'danger'}>{u.isActive ? 'aktiv' : 'inaktiv'}</Badge></td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button className="btn btn-ghost" onClick={() => openEditUser(u)}>Bearbeiten</button>
                    {u.id !== me.id && <button className="btn btn-danger" onClick={() => deleteUser(u)}>Loeschen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {tab === 'groups' && (
        <section className="grid gap-4">
          <div className="flex justify-end">
            <button className="btn btn-primary" onClick={openNewGroup}>+ Gruppe</button>
          </div>
          {groups.map((group) => (
            <div key={group.id} className="card">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-h4">{group.name}</h2>
                  <div className="text-small text-ink-secondary">{group.description ?? 'Keine Beschreibung'} · {group._count.calendarShares} Kalenderfreigaben</div>
                </div>
                <button className="btn btn-ghost" onClick={() => openEditGroup(group)}>Bearbeiten</button>
              </div>
              <div className="grid grid-cols-[1fr,160px,auto] gap-3 mb-4">
                <select className="select" value={groupForm.userId} onChange={(e) => setGroupForm({ ...groupForm, userId: e.target.value })}>
                  <option value="">Benutzer auswaehlen</option>
                  {activeUsers.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
                </select>
                <select className="select" value={groupForm.memberRole} onChange={(e) => setGroupForm({ ...groupForm, memberRole: e.target.value as 'OWNER' | 'MEMBER' })}>
                  <option value="MEMBER">Mitglied</option>
                  <option value="OWNER">Owner</option>
                </select>
                <button className="btn btn-secondary" onClick={() => addMember(group)}>Hinzufuegen</button>
              </div>
              <div className="grid gap-2">
                {group.members.map((m) => (
                  <div key={m.id} className="bg-surface-elevated border border-border rounded-md px-3 py-2 flex items-center gap-3">
                    <span className="font-mono text-small flex-1">{m.user.email}</span>
                    <Badge tone={m.role === 'OWNER' ? 'info' : 'muted'}>{m.role}</Badge>
                    <button className="btn btn-ghost !py-1" onClick={() => removeMember(group, m.user.id)}>Entfernen</button>
                  </div>
                ))}
                {group.members.length === 0 && <div className="text-small text-ink-muted">Keine Mitglieder.</div>}
              </div>
            </div>
          ))}
        </section>
      )}

      {tab === 'mail' && (
        <section className="grid gap-6">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h4">Mailserver</h2>
              {mailSettings?.hasPassword && <span className="badge badge-success">Passwort gespeichert</span>}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="label">SMTP Host</label>
                <input className="input" value={mailForm.host} onChange={(e) => setMailForm({ ...mailForm, host: e.target.value })} />
              </div>
              <div>
                <label className="label">Port</label>
                <input type="number" className="input" value={mailForm.port} onChange={(e) => setMailForm({ ...mailForm, port: Number(e.target.value) })} />
              </div>
              <label className="flex items-center gap-2 text-body mt-7">
                <input type="checkbox" checked={mailForm.secure} onChange={(e) => setMailForm({ ...mailForm, secure: e.target.checked })} />
                TLS direkt
              </label>
              <div>
                <label className="label">SMTP User</label>
                <input className="input" value={mailForm.user} onChange={(e) => setMailForm({ ...mailForm, user: e.target.value })} />
              </div>
              <div>
                <label className="label">SMTP Passwort</label>
                <input type="password" className="input" value={mailForm.pass} placeholder={mailSettings?.hasPassword ? 'unveraendert' : ''} onChange={(e) => setMailForm({ ...mailForm, pass: e.target.value })} />
              </div>
              <div>
                <label className="label">From</label>
                <input className="input" value={mailForm.from} onChange={(e) => setMailForm({ ...mailForm, from: e.target.value })} />
              </div>
              <div>
                <label className="label">Reply-To</label>
                <input className="input" value={mailForm.replyTo} onChange={(e) => setMailForm({ ...mailForm, replyTo: e.target.value })} />
              </div>
              <div className="flex items-end">
                <button className="btn btn-primary w-full" onClick={saveMailSettings}>Speichern</button>
              </div>
            </div>
            <div className="grid grid-cols-[1fr,auto,auto] gap-3 items-end mt-5 pt-5 border-t border-border">
              <div>
                <label className="label">Test-Mail an</label>
                <input className="input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="empfaenger@example.com" />
              </div>
              <button className="btn btn-secondary" onClick={verifySmtp}>Pruefen</button>
              <button className="btn btn-primary" onClick={sendTest}>Senden</button>
            </div>
          </div>
          <div className="card">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-h4">IMAP-Antwortverarbeitung</h2>
                <p className="mt-1 text-small text-ink-muted">Mailbox-Polling fuer RSVP-Antworten und Bounces.</p>
              </div>
              <div className="flex gap-2">
                <Badge tone={imapStatus?.configured ? 'success' : imapStatus?.enabled ? 'warning' : 'muted'}>
                  {imapStatus?.configured ? (imapStatus.running ? 'pollt' : 'bereit') : imapStatus?.enabled ? 'unvollstaendig' : 'deaktiviert'}
                </Badge>
                <button className="btn btn-secondary !py-1.5" onClick={loadImapStatus}>Aktualisieren</button>
              </div>
            </div>
            {imapStatus && (
              <div className="mt-4 grid gap-3 lg:grid-cols-4">
                <InfoTile label="Mailbox" value={imapStatus.host ? `${imapStatus.host} / ${imapStatus.folder}` : 'nicht konfiguriert'} />
                <InfoTile label="Post-Processing" value={imapStatus.postProcessAction === 'archive' ? `Archiv: ${imapStatus.archiveFolder || 'fehlt'}` : imapStatus.postProcessAction} />
                <InfoTile label="Letzter Lauf" value={imapStatus.lastFinishedAt ? new Date(imapStatus.lastFinishedAt).toLocaleString('de-DE') : 'noch keiner'} />
                <InfoTile label="Intervall" value={`${Math.round(imapStatus.intervalMs / 1000)} s`} />
              </div>
            )}
            {imapStatus?.lastResult && (
              <div className="mt-3 grid gap-3 lg:grid-cols-5">
                <InfoTile label="Mails" value={String(imapStatus.lastResult.seenMessages)} />
                <InfoTile label="REPLY" value={String(imapStatus.lastResult.replyParts)} />
                <InfoTile label="verarbeitet" value={String(imapStatus.lastResult.ingested)} />
                <InfoTile label="Bounces" value={String(imapStatus.lastResult.bounces)} />
                <InfoTile label="Fehler" value={String(imapStatus.lastResult.failed)} tone={imapStatus.lastResult.failed ? 'danger' : undefined} />
              </div>
            )}
            {imapStatus?.lastError && (
              <div className="mt-3 rounded-md border border-status-danger/35 bg-status-danger/10 px-3 py-2 font-mono text-small text-status-danger">
                {imapStatus.lastError}
              </div>
            )}
          </div>
          <div className="card">
            <div className="grid grid-cols-[220px,1fr] gap-4">
              <div>
                <label className="label">Template</label>
                <select
                  className="select"
                  value={templateKind}
                  onChange={(e) => {
                    setTemplateKind(e.target.value);
                    setTemplatePreview(null);
                  }}
                >
                  {templates.map((t) => <option key={t.kind} value={t.kind}>{t.kind}</option>)}
                </select>
                <label className="mt-4 flex items-center gap-2 text-body">
                  <input type="checkbox" checked={selectedTemplate?.enabled !== false} onChange={(e) => updateTemplate({ enabled: e.target.checked })} />
                  aktiv
                </label>
                <div className="mt-4 grid gap-2">
                  <button className="btn btn-secondary w-full" onClick={() => previewTemplate()}>Preview</button>
                  <input className="input font-mono" value={templateTestTo} onChange={(e) => setTemplateTestTo(e.target.value)} placeholder="test@example.com" />
                  <button className="btn btn-primary w-full" onClick={sendTemplateTest}>Test senden</button>
                </div>
              </div>
              {selectedTemplate && (
                <div className="grid gap-3">
                  <div>
                    <label className="label">Betreff</label>
                    <input className="input" value={selectedTemplate.subject} onChange={(e) => updateTemplate({ subject: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Text</label>
                    <textarea className="textarea font-mono" rows={7} value={selectedTemplate.text} onChange={(e) => updateTemplate({ text: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">HTML</label>
                    <textarea className="textarea font-mono" rows={7} value={selectedTemplate.html ?? ''} onChange={(e) => updateTemplate({ html: e.target.value })} />
                  </div>
                  <div className="flex justify-end">
                    <button className="btn btn-primary" onClick={saveTemplate}>Template speichern</button>
                  </div>
                  {templatePreview && (
                    <div className="grid gap-3 rounded-md border border-border bg-bg/25 p-3">
                      <div>
                        <label className="label">Preview Betreff</label>
                        <div className="rounded-md border border-border bg-surface-elevated px-3 py-2 font-mono text-small">{templatePreview.subject}</div>
                      </div>
                      <div>
                        <label className="label">Preview Text</label>
                        <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface-elevated p-3 font-mono text-small text-ink-secondary">{templatePreview.text}</pre>
                      </div>
                      {templatePreview.html && (
                        <div>
                          <label className="label">Preview HTML</label>
                          <iframe title="Mailtemplate Preview" className="h-72 w-full rounded-md border border-border bg-white" srcDoc={templatePreview.html} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="card !p-0 overflow-hidden">
            <div className="p-5 border-b border-border"><h2 className="text-h4">Versandhistorie</h2></div>
            <table className="w-full text-body">
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id} className="table-row align-top">
                    <td className="px-4 py-3 font-mono text-small">{d.toEmail}</td>
                    <td className="px-4 py-3">{d.event?.summary ?? d.method}</td>
                    <td className="px-4 py-3"><Badge tone={deliveryTone(d.status)}>{d.status.toLowerCase()}</Badge></td>
                    <td className="px-4 py-3 text-ink-secondary">{new Date(d.sentAt ?? d.failedAt ?? d.queuedAt).toLocaleString('de-DE')}</td>
                    <td className="px-4 py-3 text-status-danger text-small">{d.errorMessage ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card !p-0 overflow-hidden">
            <div className="p-5 border-b border-border"><h2 className="text-h4">Bounce / DSN</h2></div>
            <table className="w-full text-body">
              <tbody>
                {bounces.map((b) => (
                  <tr key={b.id} className="table-row align-top">
                    <td className="px-4 py-3 font-mono text-small">{b.toEmail ?? '—'}</td>
                    <td className="px-4 py-3">{b.delivery?.event?.summary ?? b.subject ?? 'Bounce'}</td>
                    <td className="px-4 py-3 text-status-danger text-small">{b.diagnostic ?? 'Keine Diagnose'}</td>
                    <td className="px-4 py-3 text-ink-secondary">{new Date(b.createdAt).toLocaleString('de-DE')}</td>
                  </tr>
                ))}
                {bounces.length === 0 && <tr><td className="text-center text-ink-muted py-8 text-small">Keine Bounces erfasst.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'history' && (
        <section className="card !p-0 overflow-hidden">
          <div className="p-5 border-b border-border"><h2 className="text-h4">System- und Termin-Historie</h2></div>
          <table className="w-full text-body">
            <tbody>
              {history.map((item) => (
                <tr key={item.id} className="table-row align-top">
                  <td className="px-4 py-3 font-mono text-small text-ink-muted">{new Date(item.createdAt).toLocaleString('de-DE')}</td>
                  <td className="px-4 py-3"><Badge tone="muted">{item.action}</Badge></td>
                  <td className="px-4 py-3">
                    <div>{item.message}</div>
                    <div className="text-small text-ink-muted">{item.event?.summary ?? ''}</div>
                  </td>
                  <td className="px-4 py-3 text-small text-ink-secondary">{item.user?.name ?? item.user?.email ?? ''}</td>
                </tr>
              ))}
              {history.length === 0 && <tr><td className="text-center text-ink-muted py-8 text-small">Noch keine Historie.</td></tr>}
            </tbody>
          </table>
        </section>
      )}

      {tab === 'caldav' && (
        <section className="grid gap-4">
          <div className="card">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-h4">CalDAV-Zugriffe</h2>
                <p className="mt-1 text-small text-ink-muted">Letzte Client-Versuche inklusive Auth-Ergebnis, Pfad, Methode und User-Agent.</p>
              </div>
              <button className="btn btn-secondary" onClick={load}>Aktualisieren</button>
            </div>
          </div>
          <section className="card !p-0 overflow-hidden">
            <table className="w-full text-body">
              <thead className="bg-surface-elevated text-meta uppercase tracking-[0.16em] text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-3">Zeit</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Methode</th>
                  <th className="text-left px-4 py-3">Pfad</th>
                  <th className="text-left px-4 py-3">Konto</th>
                  <th className="text-left px-4 py-3">Client</th>
                </tr>
              </thead>
              <tbody>
                {caldavLogs.map((log) => (
                  <tr key={log.id} className="table-row align-top">
                    <td className="px-4 py-3 font-mono text-small text-ink-muted">{new Date(log.createdAt).toLocaleString('de-DE')}</td>
                    <td className="px-4 py-3">
                      <Badge tone={log.statusCode < 300 ? 'success' : log.statusCode === 401 ? 'danger' : log.statusCode < 400 ? 'info' : 'warning'}>
                        {log.statusCode} · {log.outcome}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-small">{log.method}{log.depth ? ` D:${log.depth}` : ''}</td>
                    <td className="px-4 py-3 font-mono text-small text-ink-secondary break-all">{log.path}</td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-small">{log.email ?? log.user?.email ?? '—'}</div>
                      {log.appPasswordHint && <div className="text-small text-ink-muted">Token {log.appPasswordHint}…</div>}
                    </td>
                    <td className="px-4 py-3 text-small text-ink-secondary">
                      <div className="max-w-[320px] break-words">{log.userAgent ?? '—'}</div>
                      {log.remoteAddress && <div className="mt-1 font-mono text-[11px] text-ink-muted">{log.remoteAddress}</div>}
                    </td>
                  </tr>
                ))}
                {caldavLogs.length === 0 && <tr><td colSpan={6} className="text-center text-ink-muted py-8 text-small">Noch keine CalDAV-Zugriffe erfasst.</td></tr>}
              </tbody>
            </table>
          </section>
        </section>
      )}

      <Modal open={userOpen} onClose={() => setUserOpen(false)} title={editingUser ? 'Benutzer bearbeiten' : 'Benutzer anlegen'} footer={
        <>
          <button className="btn btn-ghost" onClick={() => setUserOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={saveUser}>Speichern</button>
        </>
      }>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">E-Mail</label>
            <input className="input font-mono" value={userForm.email} disabled={!!editingUser} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
          </div>
          <div>
            <label className="label">Name</label>
            <input className="input" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
          </div>
          <div>
            <label className="label">Rolle</label>
            <select className="select" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value as 'USER' | 'ADMIN' })}>
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Organisator-Adresse</label>
            <input className="input font-mono" value={userForm.organizerEmail} onChange={(e) => setUserForm({ ...userForm, organizerEmail: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label">{editingUser ? 'Neues Passwort setzen' : 'Initiales Passwort'}</label>
            <input type="password" className="input" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-body">
            <input type="checkbox" checked={userForm.isActive} onChange={(e) => setUserForm({ ...userForm, isActive: e.target.checked })} />
            Aktiv
          </label>
        </div>
      </Modal>

      <Modal open={groupOpen} onClose={() => setGroupOpen(false)} title={editingGroup ? 'Gruppe bearbeiten' : 'Gruppe anlegen'} footer={
        <>
          <button className="btn btn-ghost" onClick={() => setGroupOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={saveGroup}>Speichern</button>
        </>
      }>
        <div className="grid gap-4">
          <div>
            <label className="label">Name</label>
            <input className="input" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} />
          </div>
          <div>
            <label className="label">Beschreibung</label>
            <textarea className="textarea" value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'warning' }) {
  return (
    <div className="card">
      <div className="text-meta uppercase tracking-[0.16em] text-ink-muted">{label}</div>
      <div className={`mt-2 text-h4 ${tone === 'warning' ? 'text-status-warning' : ''}`}>{value}</div>
    </div>
  );
}

function InfoTile({ label, value, tone }: { label: string; value: string; tone?: 'danger' }) {
  return (
    <div className="rounded-md border border-border bg-surface-elevated px-3 py-2">
      <div className="text-meta uppercase tracking-[0.14em] text-ink-muted">{label}</div>
      <div className={`mt-1 break-words font-mono text-small ${tone === 'danger' ? 'text-status-danger' : 'text-ink-primary'}`}>{value}</div>
    </div>
  );
}

function auditQueryString(filter: { type: string; status: string; q: string; limit: string }) {
  const params = new URLSearchParams();
  if (filter.type) params.set('type', filter.type);
  if (filter.status.trim()) params.set('status', filter.status.trim());
  if (filter.q.trim()) params.set('q', filter.q.trim());
  params.set('limit', filter.limit || '150');
  return params.toString();
}

function auditTypeLabel(type: AuditItem['type']) {
  if (type === 'event') return 'Historie';
  if (type === 'caldav') return 'CalDAV';
  if (type === 'delivery') return 'Mail';
  if (type === 'bounce') return 'Bounce';
  return 'Abo';
}

function auditTone(severity: AuditItem['severity']) {
  if (severity === 'danger') return 'danger';
  if (severity === 'warning') return 'warning';
  if (severity === 'success') return 'success';
  return 'info';
}
